import React, { useEffect, useState } from "react";
import ProductAPI from "../../../api/inventory/ProductApi";
import { showError, showSuccess } from "../../../utils/toastUtils";

const FinalSubmit = ({ onSubmit, prevStep, nextStep, productId }) => {
  if (!productId) {
    productId = sessionStorage.getItem("productId");
  }

  const [data, setData] = useState(null);
  const [mainImage, setMainImage] = useState("");

  useEffect(() => {
    fetchDetails();
  }, []);

  const fetchDetails = async () => {
    try {
      const res = await ProductAPI.preview(productId);
      if (res.code === 200) {
        const { product, detail, variants, media, pricing } = res.data;

        const gallery = [
          product.thumbnail,
          ...(media.images || []).map((img) => img),
        ];

        setMainImage(gallery[0] || "");
        setData({ product, detail, variants, gallery, media });
      }
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

  const product = data?.product;
  const detail = data?.detail;
  const variants = data?.variants;
  const gallery = data?.gallery;
  const media = data?.media;

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const res = await ProductAPI.finalSubmit({ productId });

      if (res.code === 200) {
        showSuccess(res.message);
        sessionStorage.removeItem("productId");
        sessionStorage.removeItem("product");
        onSubmit(productId);
      } else {
        showError(res.message);
      }
    } catch (err) {}
  };

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return "";
    const match = url.match(
      /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([^&\n?#]+)/,
    );
    
    return match ? `https://www.youtube.com/embed/${match[1]}` : "";
  };

  const groupedKeyFeatures = (detail?.keyFeatures || []).reduce((acc, item) => {
    const groupName = item.group || "Other";

    if (!acc[groupName]) {
      acc[groupName] = [];
    }

    acc[groupName].push(item);
    return acc;
  }, {});

  const formatPriceRange = (priceObj) => {
    if (!priceObj) return "";

    const { min, max } = priceObj;

    if (min == null || max == null) return "";

    return min === max ? `₹${min}` : `₹${min} – ₹${max}`;
  };

  return (
    <>
      <div className="card-header d-flex align-items-center justify-content-between">
        <h4>Product Overview</h4>
      </div>
      <div className="card-body">
        <form method="post" onSubmit={handleSubmit}>
          <div className="row g-4">
            {/* Left: Image gallery */}
            <div className="col-md-4">
              <div className="text-center mb-3">
                <img
                  src={mainImage || null}
                  alt="mainImage"
                  className="img-fluid border rounded"
                  style={{ maxHeight: "400px", objectFit: "contain" }}
                />
              </div>
              {gallery && (
                <div className="d-flex flex-wrap gap-2 justify-content-center">
                  {gallery.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt={`thumb-${idx}`}
                      className={`img-thumbnail ${
                        img === mainImage ? "border-primary" : ""
                      }`}
                      style={{
                        width: "100px",
                        height: "100px",
                        objectFit: "cover",
                        cursor: "pointer",
                      }}
                      onClick={() => setMainImage(img)}
                    />
                  ))}
                </div>
              )}

              {media?.videos?.length > 0 &&
                (() => {
                  const firstVideo = media.videos[0];
                  const videoUrl = firstVideo || "";

                  const isYoutube =
                    videoUrl.includes("youtube.com") ||
                    videoUrl.includes("youtu.be");

                  return (
                    <>
                      <h5 className="mt-4">Product Video</h5>

                      {isYoutube ? (
                        <div className="ratio ratio-16x9">
                          <iframe
                            src={getYoutubeEmbedUrl(videoUrl)}
                            title="Product Video"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      ) : (
                        <video
                          controls
                          className="w-100 rounded"
                          style={{
                            width: "100%",
                            objectFit: "cover",
                          }}
                        >
                          <source src={videoUrl} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      )}
                    </>
                  );
                })()}
            </div>

            {/* Right: Details */}
            <div className="col-md-8">
              <h2 className="fw-bold mb-3">{product?.title}</h2>
              <p className="mb-1">Product Type: {product?.productType}</p>

              {product?.brand && (
                <p className=" mb-1">
                  <span>
                    Brand: <strong>{product.brand.name}</strong>
                  </span>
                </p>
              )}

              {product?.category && (
                <p className="">
                  <span>
                    Category: <strong>{product.category.name}</strong>
                  </span>
                </p>
              )}

              {detail?.pricing?.price && (
                <div className="product-price">
                  <span className="fw-bold text-success">
                    {formatPriceRange(detail.pricing.price)}
                  </span>
                </div>
              )}

              <p>{detail?.shortDescription}</p>

              <hr />

              {Object.keys(groupedKeyFeatures).length > 0 && (
                <>
                  <h5 className="mb-2">Key Features</h5>

                  {Object.entries(groupedKeyFeatures).map(
                    ([groupName, features]) => (
                      <div key={groupName} className="mb-3">
                        <h6 className="fw-bold text-primary">{groupName}</h6>
                        <ul className="mb-0">
                          {features.map((kf) => (
                            <li className="mb-1" key={kf.key}>
                              <b>{kf.label}:</b> {kf.value}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ),
                  )}
                </>
              )}

              {/* Variants */}
              {variants?.length > 0 && (
                <>
                  <h5 className="mt-4">Variants</h5>
                  <div className="table-responsive">
                    <table className="table table-bordered text-center">
                      <thead className="table-light">
                        <tr>
                          <th>SKU</th>
                          <th>costPrice</th>
                          <th>MRP</th>
                          <th>Price</th>
                          <th>Discount</th>
                          <th>Stock</th>
                          <th>AlertQuantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {variants.map((v) => (
                          <tr key={v._id}>
                            <td>{v.sku}</td>
                            <td>₹{v.costPrice}/-</td>
                            <td>₹{v.mrp}/-</td>
                            <td>₹{v.price}/-</td>
                            <td>{v.discount}%</td>
                            <td>{v.stock}</td>
                            <td>{v.alertQuantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {detail?.description && (
                <>
                  <h5 className="mt-4">Description</h5>
                  <div
                    className="small text-justify"
                    dangerouslySetInnerHTML={{ __html: detail?.description }}
                  />
                </>
              )}

              <div className="row mt-3">
                <div className="col-md-12 text-end">
                  <button
                    type="submit"
                    id="submitBtn"
                    className="btn btn-primary shadow-none"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default FinalSubmit;
